import React, { useEffect, useState } from "react";
import Loader from "../components/UI/loader";
import { ref, getDatabase, onValue, get } from "firebase/database";
import { useParams } from "react-router-dom";

function toArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return Object.values(val);
}

// ─── Restaurant-style menu list ───────────────────────────────────────────────
function MenuList({ menu }) {
  const categories = menu?.categories
    ? toArray(menu.categories).filter((c) => c.isEnabled)
    : null;
  const flatItems =
    !menu?.categories && menu?.items ? toArray(menu.items) : null;

  if (categories && categories.length > 0) {
    return (
      <>
        {categories.map((cat) => {
          const items = toArray(cat.items || {}).filter((i) => i.isEnabled);
          if (!items.length) return null;
          return (
            <div key={cat.id} className="mb-5">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 px-1">
                {cat.name}
              </p>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                {items.map((item, idx) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between px-4 py-3 ${
                      idx < items.length - 1 ? "border-b border-gray-50" : ""
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {item.emoji && (
                        <span className="text-xl flex-shrink-0">{item.emoji}</span>
                      )}
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {item.name}
                      </span>
                    </div>
                    {item.price && (
                      <span className="text-sm font-bold text-primary ml-3 flex-shrink-0">
                        {item.price}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </>
    );
  }

  if (flatItems && flatItems.length > 0) {
    return (
      <div className="mb-5">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 px-1">
          Menu
        </p>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {flatItems.map((item, idx) => (
            <div
              key={item.id}
              className={`flex items-center justify-between px-4 py-3 ${
                idx < flatItems.length - 1 ? "border-b border-gray-50" : ""
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                {item.emoji && (
                  <span className="text-xl flex-shrink-0">{item.emoji}</span>
                )}
                <span className="text-sm font-medium text-gray-800 truncate">
                  {item.name}
                </span>
              </div>
              {item.price && (
                <span className="text-sm font-bold text-primary ml-3 flex-shrink-0">
                  {item.price}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <p className="text-sm text-gray-400 text-center py-8">No menu items added yet.</p>
  );
}

// ─── Queue card ───────────────────────────────────────────────────────────────
function QueueCard({ custom, i, getInitial, getDisplayName, calculateWaitingTime }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary-bg flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-primary">
              {getInitial(custom)}
            </span>
          </div>
          <div>
            <p className="font-semibold text-sm text-gray-900">
              {getDisplayName(custom)}
            </p>
            <div className="flex items-center gap-1 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
              <span className="text-xs text-gray-500">Waiting</span>
            </div>
          </div>
        </div>
        <span className="text-xs bg-primary-bg text-primary px-2.5 py-1 rounded-lg font-bold flex-shrink-0">
          #{custom.tokenNumber || i + 1}
        </span>
      </div>
      <div className="flex items-center gap-4 text-xs text-gray-500 mt-3 bg-gray-50 rounded-xl px-3 py-2">
        <div>
          <span className="text-gray-400">Waited: </span>
          {calculateWaitingTime(custom.timeStamp)}
        </div>
        <div>
          <span className="text-gray-400">Est: </span>
          {custom.estimate}m
        </div>
        <div>
          <span className="text-gray-400">Guests: </span>
          {custom.count}
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
function PublicTable() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maskName, setMaskName] = useState(false);
  const [menu, setMenu] = useState(null);
  const [activeTab, setActiveTab] = useState("queue");
  const database = getDatabase();
  const { id } = useParams();

  useEffect(() => {
    get(ref(database, `publicSettings/${id}`)).then((snapshot) => {
      if (snapshot.exists() && snapshot.val().maskName) setMaskName(true);
    });
    const unsubscribeMenu = onValue(ref(database, `menu/${id}`), (snap) => {
      setMenu(snap.exists() ? snap.val() : null);
    });
    return () => unsubscribeMenu();
  }, [id, database]);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onValue(ref(database, "customers"), (snapshot) => {
      setLoading(false);
      const data = snapshot.val();
      if (data) {
        const today = new Date().toDateString();
        setCustomers(
          Object.values(data).filter(
            (p) =>
              (p.ownerId === id || p.id === id) &&
              p.status === "waiting" &&
              new Date(p.timeStamp).toDateString() === today
          )
        );
      } else {
        setCustomers([]);
      }
    });
    return () => unsubscribe();
  }, [id, database]);

  const calculateWaitingTime = (ts) => {
    const mins = Math.floor((Date.now() - new Date(ts)) / 60000);
    return `${mins}m`;
  };

  const getDisplayName = (c) =>
    maskName && c.tokenNumber ? `Token #${c.tokenNumber}` : c.userName;

  const getInitial = (c) =>
    maskName && c.tokenNumber
      ? `#${c.tokenNumber}`
      : c.userName
      ? c.userName.charAt(0).toUpperCase()
      : "?";

  const hasMenu =
    menu?.categories
      ? toArray(menu.categories).some((c) => c.isEnabled && toArray(c.items || {}).some((i) => i.isEnabled))
      : menu?.items && toArray(menu.items).length > 0;

  const logoSrc = menu?.logoBase64 || "logo.png";

  return (
    <div className="min-h-screen bg-surface">
      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <img
            src={logoSrc}
            alt={menu?.restaurantName || "WaitLessQ"}
            className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-gray-100"
          />
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">
              {menu?.restaurantName || "Today's Waitlist"}
            </h1>
            <p className="text-sm text-gray-500">
              {customers.length > 0
                ? `${customers.length} ${customers.length === 1 ? "person" : "people"} waiting`
                : "No one waiting right now"}
            </p>
          </div>
        </div>
      </div>

      {/* ── Announcement ── */}
      {menu?.announcement && (
        <div className="max-w-5xl mx-auto px-4 mt-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <p className="text-sm text-amber-800 font-medium">
              📢 {menu.announcement}
            </p>
          </div>
        </div>
      )}

      {/* ── Mobile tab bar (hidden on md+) ── */}
      {hasMenu && (
        <div className="md:hidden sticky top-0 z-10 bg-white border-b border-gray-200 mt-3">
          <div className="flex">
            <button
              onClick={() => setActiveTab("queue")}
              className={`flex-1 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "queue"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-400"
              }`}
            >
              Queue {customers.length > 0 && `(${customers.length})`}
            </button>
            <button
              onClick={() => setActiveTab("menu")}
              className={`flex-1 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "menu"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-400"
              }`}
            >
              Menu
            </button>
          </div>
        </div>
      )}

      {/* ── Body: mobile tabs / desktop two-column ── */}
      <div className="max-w-5xl mx-auto md:grid md:grid-cols-2 md:gap-0 md:items-start mt-4 md:mt-5 pb-10">

        {/* Queue column */}
        <div
          className={`px-4 ${
            hasMenu && activeTab !== "queue" ? "hidden md:block" : ""
          } md:pr-5 md:border-r md:border-gray-100`}
        >
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
            Today's Queue
          </p>
          {customers.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              {customers.map((custom, i) => (
                <QueueCard
                  key={i}
                  custom={custom}
                  i={i}
                  getInitial={getInitial}
                  getDisplayName={getDisplayName}
                  calculateWaitingTime={calculateWaitingTime}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-gray-700">No one waiting</p>
              <p className="text-xs text-gray-400 mt-1">Check back later</p>
            </div>
          )}
        </div>

        {/* Menu column */}
        {hasMenu && (
          <div
            className={`px-4 ${
              activeTab !== "menu" ? "hidden md:block" : ""
            } md:pl-5`}
          >
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
              Our Menu
            </p>
            <MenuList menu={menu} />
          </div>
        )}
      </div>

      {loading && <Loader loading={loading} />}
    </div>
  );
}

export default PublicTable;
